<template>
  <div>

    <v-row>
      <v-col >
        <v-card elevation=0 class="vb-branding pa-4">
          <v-form ref="form" v-on:submit.prevent="formSubmit" v-bind:disabled="false" autocomplete="off">

            <v-row>
              <v-col cols="12" md=8 offset-md=2 lg=6 offset-lg=3>
                <v-chip v-if="selectedLogoError" size="small" class="bg-error mb-2">
                  <v-btn icon size="x-small" v-on:click="selectedLogoError = ''" data-cy="tools-createNode-client-logo-closeOnErr-btn">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  {{selectedLogoError}}
                </v-chip>

                <v-chip v-if="selectedLogo" class="mb-2" data-cy="tools-createNode-client-logo-name">
                  <v-btn variant="text" size="x-small" v-on:click="clearSelectedLogo" data-cy="tools-createNode-client-logo-clearSelected-btn">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  {{selectedLogo.name}}({{ formatSize(selectedLogo.size, 0) }})
                </v-chip>

                <div class="logo-wrapper align-center" v-if="getLogoCss">
                  <div v-bind:style="getLogoCss" data-cy="tools-createNode-client-logo-showLogo">
                    &nbsp;
                  </div>

                  <v-btn class="bg-error" size="small" v-on:click="removeClientLogo" data-cy="tools-createNode-client-logo-remove-btn">
                    <v-icon size="x-small">mdi-delete</v-icon>
                    {{$t('menus.branding.removeLogo')}}
                  </v-btn>
                </div>

                <input type="file" ref="fileInput" v-on:change="fileSelected" style="display: none" accept="image/png, image/gif, image/jpeg, image/webp" data-cy="tools-createNode-client-logo-inputField">
                <v-btn v-if="!selectedLogo" v-on:click="selectLogoClick" block color="secondary" elevation=0 data-cy="tools-createNode-client-logo-selectLogo-btn">
                  {{$t('menus.branding.selectLogo')}}
                </v-btn>

                <div class="upload-logo-info">
                  <v-icon>mdi-info-circle</v-icon>
                  {{$t('menus.branding.selectLogoInfo')}}
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md=8 offset-md=2 lg=6 offset-lg=3>
                <v-text-field
                  v-model="homepage"
                  v-bind:label="$t('menus.branding.homepage')"
                  v-bind:rules="homepageRules"
                  v-bind:error-messages="homepageErrors"
                  variant="solo-filled" flat
                  required block>
                </v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md=8 offset-md=2 lg=6 offset-lg=3>
                <v-select
                v-model="theme"
                :label="$t('menus.branding.theme')"
                :items="themeItems"
                variant="solo-filled" flat
                required block
                >

                </v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="8" offset-md="2" lg="6" offset-lg="3">
                <ColorPicker
                  v-model="brandColor"
                  :label="$t('menus.branding.color')"
                  :placeholder
                  :rules="brandColorRules"
                  clearable
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="8" offset-md="2" lg="6" offset-lg="3">
                <ColorPicker
                  v-model="foregroundColor"
                  :label="$t('menus.branding.foregroundColor')"
                  :rules="foregroundColorRules"
                  clearable
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="8" offset-md="2" lg="6" offset-lg="3">
                <ColorPicker
                  v-model="foregroundAccentColor"
                  :label="$t('menus.branding.foregroundAccentColor')"
                  :rules="foregroundAccentColorRules"
                  clearable
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="8" offset-md="2" lg="6" offset-lg="3">
                <ColorPicker
                  v-model="backgroundColor"
                  :label="$t('menus.branding.backgroundColor')"
                  :rules="backgroundColorRules"
                  clearable
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md=8 offset-md=2 lg=6 offset-lg=3>
                <v-btn type="submit" color="primary" block elevation=0>
                  {{ $t('menus.branding.saveChanges') }}
                </v-btn>
              </v-col>
            </v-row>

          </v-form>
        </v-card>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useCommonStore } from '@/stores/CommonStore.js'
import { useSystemStore } from '@/stores/SystemStore.js'
import { formatSize } from '@/components/formatters.js';
import ColorPicker from '@/components/globals/ColorPicker.vue';

export default {
  name: "BrandingIndex",
  components: { ColorPicker },
  data() {
    const websiteRE = new RegExp('^(https?:\\/\\/)?'+ // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
        '(\\#[-a-z\\d_]*)?$','i'); // fragment locator

    return {
      brandInfo: null,

      selectedLogo: null,
      selectedLogoError: '',

      homepage: "",
      homepageErrors: [],
      homepageRules: [
        v => (!v || websiteRE.test(v)) || this.$t('menus.branding.homepageInvalid'),
      ],

      theme:  '',
      themeItems: [
        {value: '', title: this.$t('menus.branding.defaultTheme')},
        {value: 'light', title: this.$t('menus.branding.lightTheme')},
        {value: 'dark', title: this.$t('menus.branding.darkTheme')},
      ],

      brandColor: '#3dabff',
      brandColorRules: [
        // v => !!v || this.$t('menus.branding.brandcolor....'),
      ],

      foregroundColor: '',
      foregroundColorRules: [
        // Add rules if needed
      ],

      foregroundAccentColor: '',
      foregroundAccentColorRules: [
        // Add rules if needed
      ],

      backgroundColor: '',
      backgroundColorRules: [
        // Add rules if needed
      ],
    };
  },
  mounted: function() {
    // If the user object is not available, fetch it from the database
    if (!this.user) {
      const self = this;

      const authStore = useAuthStore();
      authStore.refreshDBUser()
        .then((user) => {
          if (this.isRootUser) {
            this.$router.push('/console/index');
            return;
          }

          this.getBrandingInfo();
          return user;
        })
        .catch((error) => {
          console.log(error);
          self.$showMessage(self.$t('backend.system-error'), { type: 'error' });
        });
    } else {
      if (this.isRootUser) {
        this.$router.push('/console/index');
        return;
      }

      this.getBrandingInfo();
    }
  },
  methods: {
    formatSize,
    getBrandingInfo: function() {
      const payload = {
        nodeid: this.accessPointId,
      };

      // const self = this;
      const systemStore = useSystemStore();
      systemStore.getBrandingInfo(payload)
          .then(this.brandingApiResponse)
          .catch(this.brandingApiError);
    },
    async formSubmit() {
      this.selectedLogoError = "";
      const { valid } = await this.$refs.form.validate(); // Assuming validate can be async/returns promise in modern Vuetify

      if (!valid || !this.accessPointId) {
        return;
      }

      const payload = {
        nodeid: this.accessPointId,
        body: {
          theme: this.theme,
          alias: this.homepage,
          color: this.brandColor || "",
          foregroundColor: this.foregroundColor || "",
          backgroundColor: this.backgroundColor || "",
          foregroundAccentColor: this.foregroundAccentColor || "",
          logo: this.selectedLogo ? this.selectedLogo : null,
        }
      };

      const systemStore = useSystemStore();

      try {
        await systemStore.saveBrandingInfo(payload);

        // Keep the setTimeout for the delay before refreshing and showing message
        setTimeout(() => {
          this.getBrandingInfo();
          this.$showMessage(this.$t('menus.branding.successfullySaved'));
        }, 3000);

      } catch (error) {
        this.brandingApiError(error);
      }
    },
    brandingApiResponse: function(response) {
      this.selectedLogo = null;
      this.selectedLogoError = "";

      if (response.data) {
        this.brandInfo = response.data;
        if (this.brandInfo.alias) {
          this.homepage = this.brandInfo.alias;
        }

        if (this.brandInfo.color) {
          this.brandColor = this.brandInfo.color;
        }

        if (this.brandInfo.theme) {
          this.theme = this.brandInfo.theme;
        }

        if (this.brandInfo.foregroundColor) {
          this.foregroundColor = this.brandInfo.foregroundColor;
        }

        if (this.brandInfo.foregroundAccentColor) {
          this.foregroundAccentColor = this.brandInfo.foregroundAccentColor;
        }

        if (this.brandInfo.backgroundColor) {
          this.backgroundColor = this.brandInfo.backgroundColor;
        }
      }
    },
    brandingApiError: function(error) {
      if (error && error.response && error.response.data && error.response.data.error) {
        const localizedMessage = this.$localizeMessage(error.response.data.error, this.$t('backend.system-error'));
        this.$showMessage(localizedMessage, { type: 'error' });
      } else {
        console.log(error);
        this.$showMessage(this.$t('backend.system-error'), { type: 'error' });
      }
    },
    selectLogoClick: function() {
      this.$refs.fileInput.click();
    },
    fileSelected: function(e) {
      this.selectedLogo = null;
      this.selectedLogoError = "";

      if (e.target.files && e.target.files[0]) {
        if (e.target.files[0].size > 2097152) {
          this.selectedLogoError = this.$t('menus.branding.theLogoIsTooLarge');
        } else {
          this.selectedLogo = e.target.files[0];
        }
      }

      this.$refs.fileInput.value = null;
    },
    clearSelectedLogo: function() {
      this.selectedLogo = null;
      this.selectedLogoError = "";
    },
    removeClientLogo: function() {
      const payload = {
        nodeid: this.accessPointId,
      };
      const systemStore = useSystemStore();
      systemStore.deleteBrandLogo(payload)
          .then(this.brandingApiResponse)
          .catch(this.brandingApiError);
    }
  },
  computed: {
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    getLogoCss: function() {
      if (this.brandInfo && this.brandInfo.logoUrl) {
        return {
          width: '150px',
          height: '100px',
          margin: 'auto',
          marginBottom: '12px',
          display: 'block',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundImage: 'url("'+this.brandInfo.logoUrl+'")',
        };
      }

      return false;
    },
    isRootUser() {
      const commonStore = useCommonStore();
      const rootFolderId = commonStore.rootFolderId;

      const authStore = useAuthStore();
      const rootNodeId =  authStore.rootNodeId;

      return rootNodeId === rootFolderId;
    },
    user: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    }
  }
}
</script>

<style>
/* .vb-branding {
  max-width: 640px;
  padding: 20px;
} */

.vb-branding .upload-logo-wrapper {
  margin: 25px 0;
}

.vb-branding .upload-logo-info {
  margin-top: 10px;
  font-style: italic;
  color: #b7b5b5;
  font-size: 0.9em;
}

.vb-branding .logo-wrapper {
  margin: 20px;
  text-align: center;
  padding-bottom: 20px;
}


</style>
